import { useLayoutEffect, useMemo, useRef } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { generateAdDivId, withContestTargeting } from '@/config/ads.config';
import AdSlot from '@/components/ads/AdSlot';
import { withEmbedBottomChrome } from '@/utils/embedChrome';
import { DESKTOP_COLUMN_FIXED } from '@/utils/desktopColumn';

/**
 * Sizes the anchored footer slot offers GAM when Admin has configured none: exactly
 * the one shape this placement is trafficked at.
 *
 * Deliberately NOT [[320,50],[728,90]]. A slot that declares 728x90 may be filled
 * with one — GAM picks from every size the slot offers — and 728px of creative cannot
 * fit a ~375px phone, so it renders cut off at the right edge. The desktop size
 * belongs in a size mapping keyed to a viewport wide enough to hold it, which ad-ops
 * can set per placement in Admin; it must not be an unconditional default.
 *
 * ### 320x100 — insurance only, and NOT the differentiator
 *
 * 320x100 is offered so that if ad-ops ever re-traffic the creative at that size the slot
 * can take the fill rather than going unsold until we ship again. It has never actually
 * been served: across ~5,700 prod iOS fills, `servedSize` was 320x50 every time.
 *
 * IT IS ALSO NOT WHY THIS SLOT RENDERS AND THE OTHER TWO DID NOT, which was the working
 * theory for a while and is worth killing off. The footer offers 320x50 + 320x100 while
 * post-entry and rules offer only 320x50, so it correlated — but `servedSize` is 320x50 on
 * all three, so the extra offered size changes nothing about how the tag lays itself out.
 * The real difference was `overflow-hidden` on the other two wrappers; see `index.css`.
 *
 * Two further readings recorded here for weeks were wrong. Do not revive either:
 *   1. "The trafficked asset is ~100px-tall art on a 320x50 declaration, with a 14px
 *      transparent top band." No — it is a third-party DCM tag, and the artwork ad-ops
 *      supplied is a correct, self-contained 320x50.
 *   2. "A second, properly built 320x50 creative in the same rotation renders complete."
 *      No — there is exactly ONE creative (138572155774, line item 7378726280) across the
 *      footer, rules AND post-entry slots. There is no rotation to explain anything by.
 *
 * The cost of listing 100, which is real: a genuinely 100px-tall creative can win the slot,
 * in a placement the product wants at 50 (per Damian, 18 Aug: "the creative should be
 * 320x50"). `FOOTER_ADVERT_SLOT_MIN_HEIGHT_PX` keeps the reserved floor at the SHORTEST
 * offered shape so a 50 fill is not left in a 100px box, and the banner measures itself and
 * grows upward. If a 100px banner is unacceptable to the client, this list is the one place
 * to take it off again.
 */
export const STICKY_FOOTER_SIZES: [number, number][] = [
  [320, 50],
  [320, 100],
];

/** Viewport width from which a size mapping is describing tablet/desktop, not a phone. */
const DESKTOP_VIEWPORT_PX = 768;

const sizeKey = ([width, height]: [number, number]) => `${width}x${height}`;

/**
 * Ensure the trafficked mobile shape is in a size list, keeping everything already
 * in it.
 *
 * Additive on purpose: nothing is removed and nothing wider than a phone is added, so
 * a desktop mapping still serves exactly what ad-ops configured, and an Admin config
 * that already lists 320x50 passes through unchanged. What this guards against is a
 * saved config that omits the one shape the placement is actually trafficked at —
 * a slot that refuses the size the buyer is buying simply goes unsold.
 */
const withMobileBannerShapes = (sizes: [number, number][]): [number, number][] => {
  const present = new Set(sizes.map(sizeKey));
  return [...sizes, ...STICKY_FOOTER_SIZES.filter(size => !present.has(sizeKey(size)))];
};

/**
 * Apply the same rule to a responsive size mapping.
 *
 * Two holes get closed here. Buckets keyed to a phone viewport are guaranteed to offer
 * the trafficked mobile shape; and a mapping whose narrowest bucket starts above 0 —
 * the saved config starts at 375 — gets one that starts at 0. GPT falls back to the
 * slot's full `defineSlot` list when no bucket matches, which on a 360px-wide phone
 * put the configured 728x90 back in play, cut off at the right edge.
 */
const withMobileBannerShapeMappings = (
  mappings: { viewport: [number, number]; sizes: [number, number][] }[] | undefined
): { viewport: [number, number]; sizes: [number, number][] }[] | undefined => {
  if (!mappings?.length) return mappings;

  const widened = mappings.map(mapping =>
    mapping.viewport[0] < DESKTOP_VIEWPORT_PX
      ? { ...mapping, sizes: withMobileBannerShapes(mapping.sizes) }
      : mapping
  );

  const coversNarrowest = widened.some(mapping => mapping.viewport[0] === 0);
  return coversNarrowest
    ? widened
    : [{ viewport: [0, 0] as [number, number], sizes: [...STICKY_FOOTER_SIZES] }, ...widened];
};

/** Vertical padding the banner puts around the slot (`py-2` => 8px top + bottom). */
const BANNER_VERTICAL_PADDING_PX = 16;

/** One side of that padding — the gap between the slot and the banner's own edge. */
const BANNER_EDGE_PADDING_PX = BANNER_VERTICAL_PADDING_PX / 2;

/**
 * The floor the slot reserves before GAM answers — the *shortest* offered creative,
 * not the tallest.
 *
 * AdSlot otherwise reserves `max(...heights)` at every breakpoint, which would hold
 * 100px open for a 50px creative and leave the banner half empty. The container has
 * no maximum, so a taller creative still grows it and is shown in full; the measured
 * height below is what keeps the reserved space honest either way. Floor low, grow to
 * fit — never the reverse.
 */
export const FOOTER_ADVERT_SLOT_MIN_HEIGHT_PX = Math.min(
  ...STICKY_FOOTER_SIZES.map(([, height]) => height)
);

/**
 * Fallback vertical space the anchored footer ad banner occupies, used before the
 * banner has measured itself (and by any consumer that renders no banner at all).
 *
 * Derived, not typed in: it cannot drift out of step with the slot floor above.
 */
export const FOOTER_ADVERT_HEIGHT_PX =
  FOOTER_ADVERT_SLOT_MIN_HEIGHT_PX + BANNER_VERTICAL_PADDING_PX;

/**
 * CSS var the mounted banner publishes its *measured* height into.
 *
 * The constant above is only an estimate: GAM decides at fill time which creative
 * to serve, and a taller one (or a 728x90 into a narrow column) makes the banner
 * grow. The banner is bottom-anchored so it grows upward and is never clipped —
 * but the space call sites reserve below their content has to grow with it, or the
 * last row scrolls under a banner that turned out taller than 106px.
 */
export const FOOTER_ADVERT_HEIGHT_VAR = '--squares-footer-advert-height';

/** The banner's live height: measured when mounted, estimated before that. */
export const FOOTER_ADVERT_HEIGHT = `var(${FOOTER_ADVERT_HEIGHT_VAR}, ${withEmbedBottomChrome(
  FOOTER_ADVERT_HEIGHT_PX
)})`;

/**
 * The banner's bottom padding — how the creative clears DK's tab bar.
 *
 * ### Why padding and not a bottom offset
 *
 * This used to be `bottom: var(--squares-embed-bottom-chrome)`, lifting the whole
 * banner clear of the bar. That made *both* directions of error a visible defect: too
 * small clipped the creative, and too large left a strip of live board content showing
 * between the banner and the bar. With no way to measure the overlap from inside the
 * webview (see `utils/embedChrome.ts`), a value had to be guessed — and a guess with no
 * safe direction had to be tuned tight, which is what cropped the creative on iOS.
 *
 * Pinning at `bottom: 0` and expressing the clearance as padding instead keeps the
 * creative in exactly the same place while changing what sits beneath it: the banner's
 * own opaque background rather than the page. Overshooting now costs a taller dark band
 * that reads as app chrome, not a gap that reads as a bug.
 *
 * That asymmetry is the whole point — it is what makes it safe to set the chrome
 * generously enough to cover the worst plausible overlap rather than the smallest one
 * we can defend. `?bottomInset` still overrides it and remains the real fix.
 */
export const FOOTER_ADVERT_BOTTOM_PADDING = withEmbedBottomChrome(BANNER_EDGE_PADDING_PX);

/**
 * Space a scrolling call site must reserve below its last row so the fixed banner
 * never overlays it.
 *
 * Just the banner height now, with no chrome added on top. The banner runs to the
 * viewport bottom and carries the chrome inside its own padding, so its measured
 * height already includes it — adding it again here would strand a second inset's
 * worth of dead scroll room under the board. The chrome is still counted exactly
 * once per stack; it is simply counted by the banner rather than by its call sites.
 */
export const FOOTER_ADVERT_RESERVED_SPACE = FOOTER_ADVERT_HEIGHT;

/** Contest-level sticky footer placement (from Squares contest metadata) */
export interface ContestStickyFooterPlacement {
  placementKey?: string;
  enabled?: boolean;
  sizes?: [number, number][];
  sizeMappings?: { viewport: [number, number]; sizes: [number, number][] }[];
  targeting?: Record<string, string>;
  adUnitPath?: string;
}

export interface FooterAdvertProps {
  /** Contest-level sticky footer placement override (from admin Ads tab) */
  contestStickyFooterPlacement?: ContestStickyFooterPlacement | null;
  /** Image URL from ad service (fallback when GAM not used) */
  imageUrl?: string;
  /** Click URL for the ad (fallback when GAM not used) */
  clickUrl?: string;
  /** Click handler (fallback when GAM not used) */
  onClick?: () => void;
  /** Alt text for accessibility */
  altText?: string;
  /** Optional aspect ratio (default 320x50 for sticky footer) */
  aspectRatio?: string;
  /** Ad slot identifier for ad service */
  slotId?: string;
  /**
   * Contest in scope, when the view has exactly one. Drives `pid`/`s5` slot-level
   * targeting on the anchored footer slot so GAM can scope the request to that contest.
   *
   * Deliberately left unset by the list views (Contests, Results, My Picks): a value
   * that changes mid-view would re-key the slot's targeting, tearing it down and
   * re-requesting it — a duplicate impression on every selection change.
   */
  contestId?: string | null;
}

/**
 * Footer Advert Component - Anchored Display Banner
 *
 * The GAM branch renders full-bleed and fixed to the embed viewport (clamped to the
 * desktop column that MainLayout establishes), pinned a constant distance above the
 * DK tab bar. It is a true anchor: its position is a single `bottom` offset and is
 * never recomputed from scroll, so the banner cannot drift up into the middle of the
 * board or slide underneath the tab bar and lose its bottom half.
 *
 * Every call site reserves FOOTER_ADVERT_RESERVED_SPACE below its last row so its
 * content — the Submit Entry CTA included — scrolls clear of the banner rather than
 * being covered by it.
 *
 * Uses GAM (Google Ad Manager) when configured via tenant STICKY_FOOTER placement.
 * Size: 320x50 (mobile) per squares_requirements.md - "Sticky Display Banner Footer"
 * Falls back to placeholder when GAM not enabled or STICKY_FOOTER not configured.
 */
export default function FooterAdvert({
  contestStickyFooterPlacement,
  imageUrl,
  clickUrl,
  onClick,
  altText = 'Advertisement',
  aspectRatio = '320/50',
  slotId = 'squares-sticky-footer',
  contestId,
}: FooterAdvertProps) {
  const { isAdsEnabled, adPlacements, isPlacementEnabled, gamConfig } = useTenant();
  const bannerRef = useRef<HTMLDivElement>(null);

  const useContestPlacement =
    contestStickyFooterPlacement?.enabled &&
    (contestStickyFooterPlacement.sizes?.length ?? 0) > 0 &&
    gamConfig?.networkId;

  const configuredPlacement = useMemo(
    () =>
      useContestPlacement
        ? {
            ...contestStickyFooterPlacement,
            adUnitPath:
              contestStickyFooterPlacement.adUnitPath ||
              `/${gamConfig!.networkId}/${contestStickyFooterPlacement.targeting?.s1 || 'dfs'}/${contestStickyFooterPlacement.targeting?.s2 || 'free-to-play'}/${contestStickyFooterPlacement.targeting?.s3 || 'sticky-banner-footer'}`,
          }
        : adPlacements?.STICKY_FOOTER,
    [useContestPlacement, contestStickyFooterPlacement, gamConfig?.networkId, adPlacements]
  );

  /**
   * Always hand AdSlot an explicit size list. Left undefined it falls back to its own
   * [[320,50],[728,90]] default, which would put a desktop creative back in play on a
   * phone; an explicitly configured size from Admin still wins.
   *
   * Memoised, as is `configuredPlacement` above. AdSlot keys its define/destroy effect on
   * the `sizes` and `targeting` *identities*, so rebuilding these objects on every render
   * destroyed and re-requested the slot each time a parent re-rendered — a duplicate
   * impression per react-query refetch. The contest targeting is layered on here rather
   * than in either branch above so both the tenant STICKY_FOOTER placement and the
   * per-contest override get it.
   *
   * The memoisation is load-bearing for the size widening too: `withMobileBannerShapes`
   * and `withMobileBannerShapeMappings` both build fresh arrays, so calling them outside
   * this `useMemo` would hand AdSlot a new `sizes` identity on every render and
   * reintroduce exactly the duplicate impression per refetch that the memo fixed.
   */
  const effectivePlacement = useMemo(
    () =>
      configuredPlacement
        ? {
            ...configuredPlacement,
            sizes: withMobileBannerShapes(
              configuredPlacement.sizes?.length ? configuredPlacement.sizes : STICKY_FOOTER_SIZES
            ),
            sizeMappings: withMobileBannerShapeMappings(configuredPlacement.sizeMappings),
            targeting: withContestTargeting(configuredPlacement.targeting, contestId),
          }
        : undefined,
    [configuredPlacement, contestId]
  );

  const showGamAd =
    isAdsEnabled &&
    (useContestPlacement ||
      (!!adPlacements?.STICKY_FOOTER && isPlacementEnabled('STICKY_FOOTER'))) &&
    !!effectivePlacement;

  /**
   * Publish the banner's real height so call sites reserve exactly the space it
   * takes, whatever creative GAM ends up filling with. Measured, not assumed: the
   * slot's own height is only known after the creative iframe is injected, which is
   * well after mount — hence the ResizeObserver rather than a one-shot read.
   */
  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!showGamAd || !el) return;

    const publish = () => {
      const measured = el.getBoundingClientRect().height;
      // Never shrink below the estimate: a collapsed/unfilled slot must not pull
      // content down under the banner the moment a creative does arrive.
      const height = Math.round(Math.max(measured, FOOTER_ADVERT_HEIGHT_PX));
      document.documentElement.style.setProperty(FOOTER_ADVERT_HEIGHT_VAR, `${height}px`);
    };

    publish();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(publish);
      observer.observe(el);
    }

    return () => {
      observer?.disconnect();
      document.documentElement.style.removeProperty(FOOTER_ADVERT_HEIGHT_VAR);
    };
  }, [showGamAd]);

  if (showGamAd && effectivePlacement) {
    return (
      <div
        ref={bannerRef}
        /*
         * `squares-footer-advert` is a styling hook, not decoration — `index.css` reaches
         * the GPT iframe through it to force a taller box on iOS. Kept as a real class
         * rather than reusing the `data-test-id`, so the selector is obviously load-bearing
         * and does not look like a test artefact someone can tidy away.
         */
        className={`squares-footer-advert fixed inset-x-0 w-full z-30 py-2 bg-[#121212] border-t border-zinc-800 flex justify-center ${DESKTOP_COLUMN_FIXED}`}
        /*
         * Flush with the bottom of the embed viewport, clearing the DK tab bar with
         * padding rather than an offset — so the space the bar covers is the banner's
         * own background instead of a hole onto the board. No `top`, no scroll
         * listener: the banner is welded to the bottom and stays fully visible at
         * every scroll position.
         *
         * Overrides `py-2`'s bottom half only; the top padding still comes from the
         * class, so the slot keeps even spacing inside whatever the banner resolves to.
         */
        style={{ bottom: 0, paddingBottom: FOOTER_ADVERT_BOTTOM_PADDING }}
        data-test-id="squares-footer-advert"
        role="banner"
        aria-label={altText}
      >
        <AdSlot
          divId={
            useContestPlacement
              ? 'squares-sticky-footer-contest'
              : generateAdDivId(adPlacements!, 'STICKY_FOOTER', false, 0)
          }
          adPlacement={effectivePlacement}
          placement="squares-sticky-footer"
          className="mx-auto"
          minHeightPx={FOOTER_ADVERT_SLOT_MIN_HEIGHT_PX}
        />
      </div>
    );
  }

  // Fallback: static image/click or placeholder
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (clickUrl) {
      window.open(clickUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="w-full mt-4 px-4">
      <div
        onClick={imageUrl || clickUrl ? handleClick : undefined}
        className={`relative w-full overflow-hidden rounded-lg max-w-[320px] mx-auto ${imageUrl || clickUrl ? 'cursor-pointer' : ''}`}
        style={{ aspectRatio, minHeight: '50px' }}
        role="banner"
        aria-label={altText}
        data-ad-slot={slotId}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={altText} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full min-h-[50px] bg-zinc-900 border border-zinc-800 border-dashed flex items-center justify-center">
            <div className="text-center">
              <p className="text-zinc-600 text-sm">Ad Slot</p>
              <p className="text-zinc-700 text-xs">{slotId}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
